import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import GetAppIcon from '@material-ui/icons/GetApp';
import RestoreIcon from '@material-ui/icons/Restore';
import ScheduleIcon from '@material-ui/icons/Schedule';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import StorageIcon from '@material-ui/icons/Storage';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { dbBackupManagePermission } from '../../permissions';

// ── Types ──

interface EnvConfig {
  key: string;
  displayName: string;
  color: string;
  namespace: string;
}

interface DatabaseEntry {
  pod: string;
  engine: string;
  database: string;
}

interface BackupRow {
  id: number;
  database_name: string;
  status: string;
  file_size: number | null;
  duration_seconds: number | null;
  anonymized: boolean;
  created_by: string;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  created_at: string;
}

interface ScheduleRow {
  id: number;
  database_name: string;
  schedule: string;
  anonymized: boolean;
  retention: number;
  created_by: string;
  created_at: string;
}

// ── Helpers ──

function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === undefined) return '-';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} Go`;
}

function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '-';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

function humanCron(cron: string): string {
  if (cron === '0 2 * * *') return 'Quotidien (02h00)';
  if (cron === '0 2 * * 0') return 'Hebdomadaire (dim. 02h00)';
  if (cron === '0 2 1 * *') return 'Mensuel (1er, 02h00)';
  return cron;
}

// ── Styles ──

const useStyles = makeStyles(theme => ({
  tabs: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    minHeight: 40,
    '& .MuiTab-root': {
      minHeight: 40,
      fontWeight: 700,
      textTransform: 'uppercase',
      fontSize: '0.75rem',
      letterSpacing: '0.06em',
    },
  },
  section: {
    padding: theme.spacing(2),
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  table: {
    '& th': {
      fontWeight: 600,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      color: theme.palette.text.secondary,
      borderBottom: `2px solid ${theme.palette.divider}`,
      padding: theme.spacing(1, 2),
    },
    '& td': {
      padding: theme.spacing(1.5, 2),
    },
  },
  dbName: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    fontWeight: 600,
  },
  empty: {
    textAlign: 'center',
    padding: theme.spacing(3),
  },
  formControl: {
    marginBottom: theme.spacing(2),
    minWidth: 200,
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: theme.spacing(2),
    minWidth: 400,
  },
  warningText: {
    color: theme.palette.error.main,
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
}));

// ── Component ──

export const DbBackupContent = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const configApi = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);
  const backendBaseUrl = configApi.getString('backend.baseUrl');

  const entityRef = `${entity.kind}:${entity.metadata.namespace ?? 'default'}/${entity.metadata.name}`;
  const annotations = entity.metadata.annotations ?? {};
  const hasDatabase = annotations['fondative.io/has-database'] === 'true';
  const { allowed: canManage } = usePermission({ permission: dbBackupManagePermission });

  const [activeTab, setActiveTab] = useState(0);
  const [envs, setEnvs] = useState<EnvConfig[]>([]);

  // Data state per env
  const [databases, setDatabases] = useState<Record<string, DatabaseEntry[]>>({});
  const [loadingDbs, setLoadingDbs] = useState<Record<string, boolean>>({});
  const [backups, setBackups] = useState<Record<string, BackupRow[]>>({});
  const [loadingBackups, setLoadingBackups] = useState<Record<string, boolean>>({});
  const [schedules, setSchedules] = useState<Record<string, ScheduleRow[]>>({});
  const [loadingSchedules, setLoadingSchedules] = useState<Record<string, boolean>>({});

  // Dialogs
  const [newBackupOpen, setNewBackupOpen] = useState(false);
  const [newScheduleOpen, setNewScheduleOpen] = useState(false);
  const [restoreConfirmOpen, setRestoreConfirmOpen] = useState(false);
  const [restoreBackupId, setRestoreBackupId] = useState<number | null>(null);

  // Backup form
  const [backupDb, setBackupDb] = useState('');
  const [backupAnonymize, setBackupAnonymize] = useState(false);
  const [backupSubmitting, setBackupSubmitting] = useState(false);

  // Schedule form
  const [schedDb, setSchedDb] = useState('');
  const [schedFrequency, setSchedFrequency] = useState('0 2 * * *');
  const [schedCustomCron, setSchedCustomCron] = useState('');
  const [schedAnonymize, setSchedAnonymize] = useState(false);
  const [schedRetention, setSchedRetention] = useState(7);
  const [schedSubmitting, setSchedSubmitting] = useState(false);

  // Restore state
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  // Polling ref
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load environments from config
  useEffect(() => {
    try {
      const arr = configApi.getConfigArray('fondative.environments');
      setEnvs(
        arr.map(c => ({
          key: c.getString('key'),
          displayName: c.getString('displayName'),
          color: c.getOptionalString('color') ?? '#1976d2',
          namespace: c.getString('namespace'),
        })),
      );
    } catch {
      setEnvs([]);
    }
  }, [configApi]);

  const currentEnv = envs[activeTab];

  // Fetch databases for current env
  useEffect(() => {
    if (!envs.length || !hasDatabase || !currentEnv) return;
    const envKey = currentEnv.key;
    if (databases[envKey] || loadingDbs[envKey]) return;

    setLoadingDbs(prev => ({ ...prev, [envKey]: true }));
    fetchApi
      .fetch(`${backendBaseUrl}/api/db-dump/databases?environment=${envKey}`)
      .then(async res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: DatabaseEntry[]) => {
        setDatabases(prev => ({ ...prev, [envKey]: data }));
        setLoadingDbs(prev => ({ ...prev, [envKey]: false }));
      })
      .catch(() => {
        setDatabases(prev => ({ ...prev, [envKey]: [] }));
        setLoadingDbs(prev => ({ ...prev, [envKey]: false }));
      });
  }, [envs, activeTab, hasDatabase, databases, loadingDbs, backendBaseUrl, fetchApi, currentEnv]);

  // Fetch backups for current env
  const fetchBackups = useCallback(
    (envKey: string) => {
      fetchApi
        .fetch(`${backendBaseUrl}/api/db-backup/backups?entityRef=${encodeURIComponent(entityRef)}&environment=${envKey}`)
        .then(async res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then((data: BackupRow[]) => {
          setBackups(prev => ({ ...prev, [envKey]: data }));
          setLoadingBackups(prev => ({ ...prev, [envKey]: false }));
        })
        .catch(() => {
          setBackups(prev => ({ ...prev, [envKey]: [] }));
          setLoadingBackups(prev => ({ ...prev, [envKey]: false }));
        });
    },
    [backendBaseUrl, fetchApi, entityRef],
  );

  // Fetch schedules for current env
  const fetchSchedules = useCallback(
    (envKey: string) => {
      fetchApi
        .fetch(`${backendBaseUrl}/api/db-backup/schedules?entityRef=${encodeURIComponent(entityRef)}&environment=${envKey}`)
        .then(async res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then((data: ScheduleRow[]) => {
          setSchedules(prev => ({ ...prev, [envKey]: data }));
          setLoadingSchedules(prev => ({ ...prev, [envKey]: false }));
        })
        .catch(() => {
          setSchedules(prev => ({ ...prev, [envKey]: [] }));
          setLoadingSchedules(prev => ({ ...prev, [envKey]: false }));
        });
    },
    [backendBaseUrl, fetchApi, entityRef],
  );

  useEffect(() => {
    if (!currentEnv) return;
    const envKey = currentEnv.key;
    if (!backups[envKey] && !loadingBackups[envKey]) {
      setLoadingBackups(prev => ({ ...prev, [envKey]: true }));
      fetchBackups(envKey);
    }
    if (!schedules[envKey] && !loadingSchedules[envKey]) {
      setLoadingSchedules(prev => ({ ...prev, [envKey]: true }));
      fetchSchedules(envKey);
    }
  }, [currentEnv, backups, loadingBackups, schedules, loadingSchedules, fetchBackups, fetchSchedules]);

  // Poll for running backups
  useEffect(() => {
    if (!currentEnv) return;
    const envKey = currentEnv.key;
    const currentBackups = backups[envKey] ?? [];
    const hasRunning = currentBackups.some(b => b.status === 'running');

    if (hasRunning) {
      if (!pollRef.current) {
        pollRef.current = setInterval(() => {
          fetchBackups(envKey);
        }, 5000);
      }
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [currentEnv, backups, fetchBackups]);

  // ── Handlers ──

  const handleCreateBackup = async () => {
    if (!currentEnv || !backupDb) return;
    setBackupSubmitting(true);
    try {
      await fetchApi.fetch(`${backendBaseUrl}/api/db-backup/backups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityRef,
          environment: currentEnv.key,
          databaseName: backupDb,
          anonymize: backupAnonymize,
        }),
      });
      setNewBackupOpen(false);
      setBackupDb('');
      setBackupAnonymize(false);
      // Refresh backups
      fetchBackups(currentEnv.key);
    } catch {
      // ignore
    } finally {
      setBackupSubmitting(false);
    }
  };

  const handleCreateSchedule = async () => {
    if (!currentEnv || !schedDb) return;
    setSchedSubmitting(true);
    const cronExpression = schedFrequency === 'custom' ? schedCustomCron : schedFrequency;
    try {
      await fetchApi.fetch(`${backendBaseUrl}/api/db-backup/schedules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityRef,
          environment: currentEnv.key,
          databaseName: schedDb,
          schedule: cronExpression,
          anonymize: schedAnonymize,
          retention: schedRetention,
        }),
      });
      setNewScheduleOpen(false);
      setSchedDb('');
      setSchedFrequency('0 2 * * *');
      setSchedCustomCron('');
      setSchedAnonymize(false);
      setSchedRetention(7);
      fetchSchedules(currentEnv.key);
    } catch {
      // ignore
    } finally {
      setSchedSubmitting(false);
    }
  };

  const handleDeleteSchedule = async (dbName: string) => {
    if (!currentEnv) return;
    const safeName = `db-backup-${dbName}-${currentEnv.key}`.toLowerCase().replace(/[^a-z0-9-]/g, '-').slice(0, 52);
    try {
      await fetchApi.fetch(
        `${backendBaseUrl}/api/db-backup/schedules/${safeName}?namespace=${currentEnv.namespace}`,
        { method: 'DELETE' },
      );
      fetchSchedules(currentEnv.key);
    } catch {
      // ignore
    }
  };

  const handleRestore = async () => {
    if (!restoreBackupId || !currentEnv) return;
    setRestoring(true);
    setRestoreError(null);
    try {
      const res = await fetchApi.fetch(
        `${backendBaseUrl}/api/db-backup/backups/${restoreBackupId}/restore`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            namespace: currentEnv.namespace,
          }),
        },
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      setRestoreConfirmOpen(false);
      setRestoreBackupId(null);
    } catch (err: any) {
      setRestoreError(err.message);
    } finally {
      setRestoring(false);
    }
  };

  // ── Render ──

  if (!hasDatabase) {
    return (
      <Box p={3}>
        <Typography color="textSecondary">
          Ajoutez l'annotation <code>fondative.io/has-database: "true"</code> pour activer les sauvegardes DB.
        </Typography>
      </Box>
    );
  }

  if (!envs.length) return null;

  const currentDbs = databases[currentEnv?.key] ?? [];
  const currentBackups = backups[currentEnv?.key] ?? [];
  const currentSchedules = schedules[currentEnv?.key] ?? [];
  const isLoadingDbs = loadingDbs[currentEnv?.key] ?? false;
  const isLoadingBackups = loadingBackups[currentEnv?.key] ?? false;
  const isLoadingSchedules = loadingSchedules[currentEnv?.key] ?? false;

  return (
    <>
      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          className={classes.tabs}
          indicatorColor="primary"
          textColor="primary"
        >
          {envs.map(env => (
            <Tab key={env.key} label={env.displayName} style={{ color: env.color }} />
          ))}
        </Tabs>
        <CardContent style={{ padding: 0 }}>
          {/* ── Section: Planification ── */}
          <Box className={classes.section}>
            <Box className={classes.sectionTitle}>
              <ScheduleIcon color="action" />
              <Typography variant="h6" style={{ fontSize: '1rem' }}>
                Planification
              </Typography>
              {canManage && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => setNewScheduleOpen(true)}
                  style={{ textTransform: 'none', marginLeft: 'auto' }}
                  disabled={isLoadingDbs || currentDbs.length === 0}
                >
                  Nouvelle planification
                </Button>
              )}
            </Box>

            {isLoadingSchedules ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : currentSchedules.length === 0 ? (
              <Typography variant="body2" color="textSecondary" style={{ padding: 8 }}>
                Aucune planification configuree
              </Typography>
            ) : (
              <Table className={classes.table} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Base de donnees</TableCell>
                    <TableCell>Frequence</TableCell>
                    <TableCell>Anonymise</TableCell>
                    <TableCell>Retention</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentSchedules.map(sched => (
                    <TableRow key={sched.id} hover>
                      <TableCell>
                        <Typography className={classes.dbName}>{sched.database_name}</Typography>
                      </TableCell>
                      <TableCell>{humanCron(sched.schedule)}</TableCell>
                      <TableCell>{sched.anonymized ? 'Oui' : 'Non'}</TableCell>
                      <TableCell>{sched.retention}</TableCell>
                      <TableCell align="right">
                        {canManage && (
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteSchedule(sched.database_name)}
                            title="Supprimer la planification"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>

          {/* ── Section: Historique des sauvegardes ── */}
          <Box className={classes.section} style={{ borderTop: '1px solid rgba(0,0,0,0.12)' }}>
            <Box className={classes.sectionTitle}>
              <StorageIcon color="action" />
              <Typography variant="h6" style={{ fontSize: '1rem' }}>
                Historique des sauvegardes
              </Typography>
              {canManage && (
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => setNewBackupOpen(true)}
                  style={{ textTransform: 'none', marginLeft: 'auto' }}
                  disabled={isLoadingDbs || currentDbs.length === 0}
                >
                  Lancer un backup maintenant
                </Button>
              )}
            </Box>

            {isLoadingBackups ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : currentBackups.length === 0 ? (
              <Typography variant="body2" color="textSecondary" style={{ padding: 8 }}>
                Aucune sauvegarde
              </Typography>
            ) : (
              <Table className={classes.table} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Base de donnees</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Taille</TableCell>
                    <TableCell>Duree</TableCell>
                    <TableCell>Anonymise</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentBackups.map(backup => (
                    <TableRow key={backup.id} hover>
                      <TableCell>
                        <Typography variant="body2" style={{ fontSize: '0.8rem' }}>
                          {backup.started_at
                            ? new Date(backup.started_at).toLocaleString('fr-FR')
                            : new Date(backup.created_at).toLocaleString('fr-FR')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography className={classes.dbName}>{backup.database_name}</Typography>
                      </TableCell>
                      <TableCell>
                        {backup.status === 'completed' && (
                          <Chip size="small" label="Termine" style={{ backgroundColor: '#4caf50', color: '#fff', fontSize: '0.7rem' }} />
                        )}
                        {backup.status === 'running' && (
                          <Chip size="small" label="En cours" style={{ backgroundColor: '#1976d2', color: '#fff', fontSize: '0.7rem' }} />
                        )}
                        {backup.status === 'failed' && (
                          <Chip size="small" label="Echoue" style={{ backgroundColor: '#d32f2f', color: '#fff', fontSize: '0.7rem' }} />
                        )}
                        {backup.status === 'pending' && (
                          <Chip size="small" label="En attente" variant="outlined" style={{ fontSize: '0.7rem' }} />
                        )}
                      </TableCell>
                      <TableCell>{formatFileSize(backup.file_size)}</TableCell>
                      <TableCell>{formatDuration(backup.duration_seconds)}</TableCell>
                      <TableCell>{backup.anonymized ? 'Oui' : 'Non'}</TableCell>
                      <TableCell align="right">
                        {backup.status === 'completed' && canManage && (
                          <Box display="flex" justifyContent="flex-end" style={{ gap: 4 }}>
                            <IconButton
                              size="small"
                              title="Telecharger"
                              onClick={() => {
                                window.open(
                                  `${backendBaseUrl}/api/db-backup/backups/${backup.id}/download`,
                                  '_blank',
                                );
                              }}
                            >
                              <GetAppIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              title="Restaurer"
                              onClick={() => {
                                setRestoreBackupId(backup.id);
                                setRestoreConfirmOpen(true);
                                setRestoreError(null);
                              }}
                            >
                              <RestoreIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                        {backup.status === 'running' && (
                          <CircularProgress size={16} />
                        )}
                        {backup.status === 'failed' && backup.error_message && (
                          <Typography variant="caption" color="error" title={backup.error_message}>
                            Erreur
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* ── Dialog: Nouveau backup ── */}
      <Dialog open={newBackupOpen} onClose={() => setNewBackupOpen(false)}>
        <DialogTitle>Lancer un backup</DialogTitle>
        <DialogContent>
          <Box className={classes.dialogContent}>
            <FormControl className={classes.formControl} fullWidth>
              <InputLabel>Base de donnees</InputLabel>
              <Select
                value={backupDb}
                onChange={e => setBackupDb(e.target.value as string)}
              >
                {currentDbs.map(db => (
                  <MenuItem key={`${db.pod}-${db.database}`} value={db.database}>
                    {db.database} ({db.engine})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={backupAnonymize}
                  onChange={e => setBackupAnonymize(e.target.checked)}
                />
              }
              label="Anonymiser les donnees"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewBackupOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateBackup}
            disabled={!backupDb || backupSubmitting}
            startIcon={backupSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {backupSubmitting ? 'Lancement...' : 'Lancer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Nouvelle planification ── */}
      <Dialog open={newScheduleOpen} onClose={() => setNewScheduleOpen(false)}>
        <DialogTitle>Nouvelle planification de sauvegarde</DialogTitle>
        <DialogContent>
          <Box className={classes.dialogContent}>
            <FormControl className={classes.formControl} fullWidth>
              <InputLabel>Base de donnees</InputLabel>
              <Select
                value={schedDb}
                onChange={e => setSchedDb(e.target.value as string)}
              >
                {currentDbs.map(db => (
                  <MenuItem key={`${db.pod}-${db.database}`} value={db.database}>
                    {db.database} ({db.engine})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl className={classes.formControl} fullWidth>
              <InputLabel>Frequence</InputLabel>
              <Select
                value={schedFrequency}
                onChange={e => setSchedFrequency(e.target.value as string)}
              >
                <MenuItem value="0 2 * * *">Quotidien (02h00)</MenuItem>
                <MenuItem value="0 2 * * 0">Hebdomadaire (dim. 02h00)</MenuItem>
                <MenuItem value="0 2 1 * *">Mensuel (1er, 02h00)</MenuItem>
                <MenuItem value="custom">Personnalise (cron)</MenuItem>
              </Select>
            </FormControl>
            {schedFrequency === 'custom' && (
              <TextField
                label="Expression cron"
                placeholder="0 2 * * *"
                value={schedCustomCron}
                onChange={e => setSchedCustomCron(e.target.value)}
                fullWidth
              />
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={schedAnonymize}
                  onChange={e => setSchedAnonymize(e.target.checked)}
                />
              }
              label="Anonymiser les donnees"
            />
            <TextField
              label="Retention (nombre de sauvegardes)"
              type="number"
              value={schedRetention}
              onChange={e => setSchedRetention(parseInt(e.target.value, 10) || 7)}
              inputProps={{ min: 1, max: 365 }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewScheduleOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateSchedule}
            disabled={!schedDb || (schedFrequency === 'custom' && !schedCustomCron) || schedSubmitting}
            startIcon={schedSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {schedSubmitting ? 'Creation...' : 'Creer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Dialog: Confirmation de restauration ── */}
      <Dialog open={restoreConfirmOpen} onClose={() => setRestoreConfirmOpen(false)}>
        <DialogTitle>Confirmer la restauration</DialogTitle>
        <DialogContent>
          <Typography className={classes.warningText}>
            Attention : cette operation va ecraser les donnees actuelles de la base de donnees avec le contenu de la sauvegarde.
          </Typography>
          <Typography variant="body2">
            Cette action est irreversible. Assurez-vous d'avoir une sauvegarde recente avant de continuer.
          </Typography>
          {restoreError && (
            <Box mt={2}>
              <Typography color="error">{restoreError}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreConfirmOpen(false)}>Annuler</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleRestore}
            disabled={restoring}
            startIcon={restoring ? <CircularProgress size={16} color="inherit" /> : <RestoreIcon />}
          >
            {restoring ? 'Restauration...' : 'Restaurer'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
